/**
 * BackendActionEditor — configuration panel for platform workflow backend actions.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { X, Settings, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { useActionEditorDraft } from '../../hooks/useActionEditorDraft';
import { normalizeActionDraft } from '../../utils/actionEditorDraftUtils';
import WorkflowStepEditorShell from './WorkflowStepEditorShell';

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const ACTION_KEY_ALIASES = {
  generate_email_verification_code: 'generate_verification_code',
  resend_email_verification_code: 'resend_verification_code',
  allow_verification_code_resend: 'resend_verification_code',
  set_agency_status_to_pending_verification: 'set_agency_status_pending_verification',
  required_backend_action_if_code_verified: 'if_code_verified',
  required_backend_action_if_code_expires: 'if_code_expires',
  required_backend_action_if_wrong_code_entered_5_times: 'if_wrong_code_entered_5_times',
  generate_password_reset_token: 'generate_secure_reset_token',
  store_hashed_reset_token: 'generate_secure_reset_token',
  mark_password_reset_token_used: 'on_password_reset_completed',
};

const LABEL_TO_ACTION_KEY = {
  'generate verification code': 'generate_verification_code',
  'store hashed verification code': 'store_hashed_verification_code',
  'send verification code email': 'send_verification_code_email',
  'set agency status to pending verification': 'set_agency_status_pending_verification',
  'if code verified': 'if_code_verified',
  'if code expires': 'if_code_expires',
  'if wrong code entered 5 times': 'if_wrong_code_entered_5_times',
  'if 5 failed attempts': 'if_wrong_code_entered_5_times',
  'allow verification code resend': 'resend_verification_code',
  'find user by email': 'find_user_by_email',
  'resolve user portal': 'resolve_user_portal',
  'generate secure reset token': 'generate_secure_reset_token',
  'build reset link': 'build_reset_link',
  'record delivery attempt': 'record_delivery_attempt',
  'on password reset completed': 'on_password_reset_completed',
  'on token expiration': 'on_token_expiration',
  'lookup user across login sources': 'find_user_by_email',
  'generate reset token': 'generate_secure_reset_token',
  'store hashed reset token': 'generate_secure_reset_token',
  'return generic response': 'password_reset_generic_response',
  'normalize email': 'normalize_auth_email',
  'required backend action: if code verified': 'if_code_verified',
  'required backend action: if code expires': 'if_code_expires',
  'required backend action: if wrong code entered 5 times': 'if_wrong_code_entered_5_times',
};

function labelLookupVariants(label) {
  const normalized = String(label || '').trim().toLowerCase();
  if (!normalized) return [];
  const variants = [normalized];
  const prefix = 'required backend action:';
  if (normalized.startsWith(prefix)) {
    const stripped = normalized.slice(prefix.length).trim();
    if (stripped) variants.push(stripped);
  }
  return variants;
}

export const BACKEND_ACTION_DEFINITIONS = {
  generate_verification_code: {
    label: 'Generate Verification Code',
    description: 'Generate a numeric verification code for agency owner email verification.',
    fields: [
      { key: 'code_length', label: 'Code Length', type: 'number', default: 6, required: true },
      { key: 'expiration_minutes', label: 'Expiration (minutes)', type: 'number', default: 15, required: true },
      { key: 'max_attempts', label: 'Max Attempts', type: 'number', default: 5, required: true },
      { key: 'output_key', label: 'Output Key', type: 'text', default: 'verification_code', required: true },
    ],
  },
  store_hashed_verification_code: {
    label: 'Store Hashed Verification Code',
    description: 'Hash the generated verification code and store only the hashed version.',
    fields: [
      { key: 'code_input_key', label: 'Code Input Key', type: 'text', default: 'verification_code', required: true },
      { key: 'hash_algorithm', label: 'Hash Algorithm', type: 'text', default: 'bcrypt', required: true },
      { key: 'storage_key', label: 'Storage Key', type: 'text', default: 'agency_verification_code_hash', required: true },
      { key: 'expires_at_key', label: 'Expires At Key', type: 'text', default: 'verification_code_expires_at', required: true },
    ],
  },
  send_verification_code_email: {
    label: 'Send Verification Code Email',
    description: 'Send the verification code to the agency owner.',
    fields: [
      { key: 'recipient_email_source', label: 'Recipient Email Source', type: 'text', default: 'agency.owner_email', required: true },
      { key: 'email_subject', label: 'Email Subject', type: 'text', default: 'Confirm your FundElite agency email', required: true },
      { key: 'email_body', label: 'Email Body', type: 'textarea', default: 'Hi {{agency_owner.first_name}},\n\nYour verification code is {{verification_code}}.', required: true },
      { key: 'code_variable', label: 'Code Variable', type: 'text', default: 'verification_code', required: false },
      { key: 'sender_name', label: 'Sender Name', type: 'text', default: 'FundElite', required: false },
    ],
  },
  set_agency_status_pending_verification: {
    label: 'Set Agency Status to Pending Verification',
    description: 'Update agency status after the verification code is sent.',
    fields: [
      { key: 'agency_id_source', label: 'Agency ID Source', type: 'text', default: 'agency.id', required: true },
      { key: 'status_value', label: 'Status Value', type: 'text', default: 'pending_verification', required: true },
      { key: 'updated_at', label: 'Updated At', type: 'text', default: 'now', required: true },
    ],
  },
  if_code_verified: {
    label: 'If Code Verified',
    description: 'Check whether the submitted code matches the stored hash.',
    fields: [
      { key: 'submitted_code_source', label: 'Submitted Code Source', type: 'text', default: 'submitted_verification_code', required: true },
      { key: 'stored_hash_source', label: 'Stored Hash Source', type: 'text', default: 'agency_verification_code_hash', required: true },
      { key: 'success_next_step', label: 'Success Next Step', type: 'text', default: '', required: false },
      { key: 'failure_next_step', label: 'Failure Next Step', type: 'text', default: '', required: false },
    ],
  },
  if_code_expires: {
    label: 'If Code Expires',
    description: 'Route the workflow if the verification code has expired.',
    fields: [
      { key: 'expires_at_source', label: 'Expires At Source', type: 'text', default: 'verification_code_expires_at', required: true },
      { key: 'expired_next_step', label: 'Expired Next Step', type: 'text', default: '', required: false },
      { key: 'valid_next_step', label: 'Valid Next Step', type: 'text', default: '', required: false },
    ],
  },
  if_wrong_code_entered_5_times: {
    label: 'If Wrong Code Entered 5 Times',
    description: 'Lock verification if the wrong code is entered too many times.',
    fields: [
      { key: 'attempts_source', label: 'Attempts Source', type: 'text', default: 'verification_attempt_count', required: true },
      { key: 'max_attempts', label: 'Max Attempts', type: 'number', default: 5, required: true },
      { key: 'lockout_minutes', label: 'Lockout Minutes', type: 'number', default: 30, required: false },
      { key: 'lockout_next_step', label: 'Lockout Next Step', type: 'text', default: '', required: false },
      { key: 'retry_next_step', label: 'Retry Next Step', type: 'text', default: '', required: false },
    ],
  },
  resend_verification_code: {
    label: 'Allow Verification Code Resend',
    description: 'Allow the agency owner to request a new verification code.',
    fields: [
      { key: 'recipient_email_source', label: 'Recipient Email Source', type: 'text', default: 'agency.owner_email', required: true },
      { key: 'expiration_minutes', label: 'Expiration (minutes)', type: 'number', default: 15, required: false },
      { key: 'max_resends', label: 'Max Resends', type: 'number', default: 3, required: false },
    ],
  },
  find_user_by_email: {
    label: 'Find User by Email',
    description: 'Search user by normalized email. Never reveals whether the email exists publicly.',
    fields: [
      { key: 'email_source', label: 'Email Source', type: 'text', default: '{{trigger.email}}', required: true },
      { key: 'normalize_email', label: 'Normalize Email', type: 'checkbox', default: true, required: false },
      { key: 'silent_if_not_found', label: 'Silent If Not Found', type: 'checkbox', default: true, required: false },
    ],
  },
  resolve_user_portal: {
    label: 'Resolve User Portal',
    description: 'Determine agency, platform, or self-repair portal and reset/login hosts.',
    fields: [
      { key: 'user_id', label: 'User ID', type: 'text', default: '{{find_user_by_email.user_id}}', required: true },
      { key: 'email', label: 'Email', type: 'text', default: '{{find_user_by_email.email}}', required: true },
    ],
  },
  generate_secure_reset_token: {
    label: 'Generate Secure Reset Token',
    description: 'Create a single-use reset token. Stores hashed token only — raw token never logged.',
    fields: [
      { key: 'user_id', label: 'User ID', type: 'text', default: '{{find_user_by_email.user_id}}', required: true },
      { key: 'email', label: 'Email', type: 'text', default: '{{find_user_by_email.email}}', required: true },
      { key: 'portal', label: 'Portal', type: 'text', default: '{{resolve_user_portal.portal}}', required: true },
      { key: 'ttl_minutes', label: 'TTL (minutes)', type: 'number', default: 30, required: false },
      { key: 'invalidate_previous', label: 'Invalidate Previous Tokens', type: 'checkbox', default: true, required: false },
    ],
  },
  build_reset_link: {
    label: 'Build Reset Link',
    description: 'Build portal-correct reset URL. Full link with token is not stored in logs.',
    fields: [
      { key: 'reset_base_url', label: 'Reset Base URL', type: 'text', default: '{{resolve_user_portal.reset_base_url}}', required: true },
      { key: 'email', label: 'Email', type: 'text', default: '{{find_user_by_email.email}}', required: true },
      { key: 'include_email', label: 'Include Email in Link', type: 'checkbox', default: true, required: false },
    ],
  },
  record_delivery_attempt: {
    label: 'Record Delivery Attempt',
    description: 'Record email delivery metadata without storing raw token or full reset URL.',
    fields: [
      { key: 'user_id', label: 'User ID', type: 'text', default: '{{find_user_by_email.user_id}}', required: true },
      { key: 'email', label: 'Email', type: 'text', default: '{{find_user_by_email.email}}', required: true },
      { key: 'portal', label: 'Portal', type: 'text', default: '{{resolve_user_portal.portal}}', required: true },
      { key: 'reset_host', label: 'Reset Host', type: 'text', default: '{{resolve_user_portal.reset_host}}', required: true },
      { key: 'provider', label: 'Provider', type: 'text', default: 'mailgun', required: false },
      { key: 'status', label: 'Status', type: 'text', default: 'sent', required: true },
      { key: 'failure_reason', label: 'Failure Reason', type: 'text', default: '', required: false },
    ],
  },
  on_password_reset_completed: {
    label: 'On Password Reset Completed',
    description: 'Mark token used and complete waiting workflow executions.',
    fields: [
      { key: 'user_id', label: 'User ID', type: 'text', default: '{{find_user_by_email.user_id}}', required: true },
      { key: 'token_id', label: 'Token ID', type: 'text', default: '{{generate_secure_reset_token.token_id}}', required: true },
      { key: 'portal', label: 'Portal', type: 'text', default: '{{resolve_user_portal.portal}}', required: false },
    ],
  },
  on_token_expiration: {
    label: 'On Token Expiration',
    description: 'Mark reset token expired when TTL elapses.',
    fields: [
      { key: 'token_id', label: 'Token ID', type: 'text', default: '{{generate_secure_reset_token.token_id}}', required: true },
      { key: 'user_id', label: 'User ID', type: 'text', default: '{{find_user_by_email.user_id}}', required: true },
    ],
  },
  password_reset_generic_response: {
    label: 'Password Reset Generic Response',
    description: 'Always return the same generic response — never reveal account existence.',
    fields: [],
  },
  normalize_auth_email: {
    label: 'Normalize Auth Email',
    description: 'Normalize email to lowercase for lookup.',
    fields: [
      { key: 'email_source', label: 'Email Source', type: 'text', default: '{{trigger.email}}', required: true },
    ],
  },
};

export function normalizeStepType(rawType) {
  const type = String(rawType || '').trim().toLowerCase();
  if (type === 'action') return 'backend_action';
  return type;
}

export function resolveBackendActionKey(step) {
  if (!step) return '';
  const config = step.config || {};
  const explicit = config.action_key || step.action_key;
  if (explicit) {
    const key = String(explicit).trim().toLowerCase();
    return ACTION_KEY_ALIASES[key] || key;
  }

  for (const variant of labelLookupVariants(step.label || step.name)) {
    if (LABEL_TO_ACTION_KEY[variant]) return LABEL_TO_ACTION_KEY[variant];
  }

  const slug = String(step.label || step.name || '').trim().toLowerCase().replace(/\s+/g, '_');
  if (BACKEND_ACTION_DEFINITIONS[slug]) return slug;
  if (BACKEND_ACTION_DEFINITIONS[ACTION_KEY_ALIASES[slug]]) return ACTION_KEY_ALIASES[slug];
  return '';
}

export function isBackendActionStep(step) {
  if (!step) return false;
  const type = normalizeStepType(step.type);
  if (type === 'backend_action') return true;
  if (step.config?.requires_backend) return true;
  return Boolean(resolveBackendActionKey(step));
}

function buildDefaultConfig(actionKey) {
  const definition = BACKEND_ACTION_DEFINITIONS[actionKey];
  if (!definition) return { action_key: actionKey };
  const config = { action_key: actionKey };
  definition.fields.forEach((field) => {
    config[field.key] = field.default ?? '';
  });
  return config;
}

function validateConfig(actionKey, config, fields) {
  const errors = {};
  (fields || []).forEach((field) => {
    if (!field.required) return;
    const value = config[field.key];
    if (value === undefined || value === null || String(value).trim() === '') {
      errors[field.key] = `${field.label} is required`;
    }
  });
  return errors;
}

const BackendActionEditor = ({
  isOpen,
  onClose,
  step,
  onSave,
  workflowId,
  token,
  isNewWorkflow = false,
  width,
  overlayMode,
  onOverlayDismiss,
  activePanelType = 'backend-action',
  chromeHeight,
}) => {
  const actionKey = useMemo(() => resolveBackendActionKey(step), [step]);
  const definition = BACKEND_ACTION_DEFINITIONS[actionKey];
  const [config, setConfig] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!step) return;
    const key = resolveBackendActionKey(step);
    const defaults = buildDefaultConfig(key);
    setConfig({ ...defaults, ...(step.config || {}), action_key: key });
    setErrors({});
  }, [step]);

  const configDraft = useMemo(() => normalizeActionDraft(config), [config]);
  const mapStepToDraft = useCallback((s) => {
    const key = resolveBackendActionKey(s);
    return normalizeActionDraft({
      ...buildDefaultConfig(key),
      ...(s?.config || {}),
      action_key: key,
    });
  }, []);
  const { guardedClose, commitDraft } = useActionEditorDraft({
    editorId: step ? `backend-${step.id}` : 'backend',
    isOpen,
    step,
    draft: configDraft,
    mapStepToDraft,
  });
  const requestClose = useCallback(() => guardedClose(onClose), [guardedClose, onClose]);

  if (!isOpen || !step) return null;

  const displayName = step.label || step.name || definition?.label || 'Backend Action';
  const fields = definition?.fields || [];

  const handleFieldChange = (key, value, type) => {
    const nextValue = type === 'number' ? Number(value) : value;
    setConfig((prev) => ({ ...prev, [key]: nextValue }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSave = async () => {
    const validationErrors = validateConfig(actionKey, config, fields);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please fix the highlighted fields');
      return;
    }

    const updatedStep = {
      ...step,
      type: 'backend_action',
      label: displayName,
      name: displayName,
      config: {
        ...config,
        action_key: actionKey,
        configured: true,
      },
    };
    const originalKey = String(step.config?.action_key || step.action_key || '').trim().toLowerCase();
    if (originalKey && originalKey !== actionKey) {
      updatedStep.config.original_action_key = originalKey;
    }

    if (!isNewWorkflow && workflowId && token) {
      setSaving(true);
      try {
        const res = await axios.patch(
          `${API}/platform/system-automations/workflows/${workflowId}/nodes/${step.id}`,
          {
            config: updatedStep.config,
            label: updatedStep.label,
            name: updatedStep.name,
            type: 'backend_action',
            action_key: actionKey,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const savedNode = res.data?.node || updatedStep;
        onSave(savedNode);
        toast.success('Action saved');
        commitDraft();
        onClose();
      } catch (err) {
        const detail = err.response?.data?.detail;
        const message = Array.isArray(detail)
          ? detail.map((item) => item.msg || item).join(', ')
          : detail || 'Failed to save action';
        toast.error(message);
      } finally {
        setSaving(false);
      }
      return;
    }

    onSave(updatedStep);
    toast.success('Action saved');
    commitDraft();
    onClose();
  };

  return (
    <WorkflowStepEditorShell
      isOpen={isOpen}
      width={width}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className="bg-zinc-900 border-l border-zinc-700"
      testId="backend-action-editor"
    >
      <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
            <Settings size={20} className="text-orange-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">{displayName}</h3>
            <p className="text-xs text-zinc-500">Backend Action</p>
          </div>
        </div>
        <button onClick={requestClose} className="p-2 hover:bg-zinc-800 rounded-lg" type="button">
          <X size={18} className="text-zinc-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        <div className="rounded-lg border border-zinc-800 bg-zinc-800/40 p-3">
          <p className="text-sm text-zinc-300">{definition?.description || 'Configure this backend action.'}</p>
          {actionKey && (
            <p className="text-xs text-zinc-500 mt-2">Action key: <span className="text-zinc-400">{actionKey}</span></p>
          )}
        </div>

        {!definition && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
            No dedicated schema found for this action. Saving will persist the current action key only.
          </div>
        )}

        {fields.map((field) => (
          <div key={field.key}>
            <Label className="text-zinc-400 text-sm">
              {field.label}
              {field.required ? ' *' : ''}
            </Label>
            {field.type === 'textarea' ? (
              <Textarea
                value={config[field.key] ?? ''}
                onChange={(e) => handleFieldChange(field.key, e.target.value, field.type)}
                className={cn('mt-2 bg-zinc-800 border-zinc-700 text-white min-h-[140px]', errors[field.key] && 'border-red-500')}
              />
            ) : (
              <Input
                type={field.type === 'number' ? 'number' : 'text'}
                value={config[field.key] ?? ''}
                onChange={(e) => handleFieldChange(field.key, e.target.value, field.type)}
                className={cn('mt-2 bg-zinc-800 border-zinc-700 text-white', errors[field.key] && 'border-red-500')}
              />
            )}
            {errors[field.key] && (
              <p className="text-xs text-red-400 mt-1">{errors[field.key]}</p>
            )}
          </div>
        ))}
      </div>

      <div className="p-4 border-t border-zinc-700 flex justify-end gap-2">
        <Button variant="outline" onClick={requestClose} className="border-zinc-600 text-zinc-300" disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} className="bg-emerald-500 hover:bg-emerald-600 text-white" disabled={saving}>
          {saving ? (
            <>
              <Loader2 size={16} className="mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Action'
          )}
        </Button>
      </div>
    </WorkflowStepEditorShell>
  );
};

export default BackendActionEditor;
